/**
 * AddressBookService - Manages user-defined nicknames for blockchain addresses
 * Provides localStorage persistence with import/export functionality
 */

const ADDRESS_BOOK_KEY = 'gamemaker_address_book';

/**
 * Validates that a given string is a valid wallet address
 * @param {string} address - Address to validate
 * @returns {boolean} - True if valid wallet address
 */
const isValidAddress = (address) => {
  if (!address || typeof address !== 'string') return false;
  return /^0x[a-fA-F0-9]{40}$/.test(address.trim());
};

/**
 * Normalizes an address to lowercase for consistent storage
 * @param {string} address - Address to normalize
 * @returns {string} - Normalized address
 */
const normalizeAddress = (address) => {
  if (!address) return '';
  return address.toLowerCase().trim();
};

/**
 * Loads the address book from localStorage
 * @returns {Object} - Address book mapping {address: nickname}
 */
const loadAddressBook = () => {
  try {
    const stored = localStorage.getItem(ADDRESS_BOOK_KEY);
    return stored ? JSON.parse(stored) : {};
  } catch (error) {
    console.error('Error loading address book:', error);
    return {};
  }
};

/**
 * Saves the address book to localStorage
 * @param {Object} addressBook - Address book mapping {address: nickname}
 */
const saveAddressBook = (addressBook) => {
  try {
    localStorage.setItem(ADDRESS_BOOK_KEY, JSON.stringify(addressBook));
  } catch (error) {
    console.error('Error saving address book:', error);
  }
};

/**
 * Gets the nickname for an address, or returns formatted address if no nickname exists
 * @param {string} address - Blockchain address
 * @returns {string} - Nickname or formatted address
 */
const getNicknameOrAddress = (address) => {
  if (!address) return '';
  
  const normalizedAddress = normalizeAddress(address);
  const addressBook = loadAddressBook();
  const nickname = addressBook[normalizedAddress];
  
  if (nickname && nickname.trim()) {
    return nickname;
  }
  
  // Return formatted address (first 6 + last 4 characters)
  if (address.length > 10) {
    return `${address.slice(0, 6)}...${address.slice(-4)}`;
  }
  
  return address;
};

/**
 * Gets the full display text with both nickname and address
 * @param {string} address - Blockchain address
 * @returns {string} - "Nickname (0x1234...5678)" or just formatted address
 */
const getDisplayText = (address) => {
  if (!address) return '';
  
  const normalizedAddress = normalizeAddress(address);
  const addressBook = loadAddressBook();
  const nickname = addressBook[normalizedAddress];
  
  const formattedAddress = address.length > 10 
    ? `${address.slice(0, 6)}...${address.slice(-4)}`
    : address;
  
  if (nickname && nickname.trim()) {
    return `${nickname} (${formattedAddress})`;
  }
  
  return formattedAddress;
};

/**
 * Adds or updates a nickname for an address
 * @param {string} address - Blockchain address
 * @param {string} nickname - Human-readable nickname
 * @returns {boolean} - True if successful
 */
const setNickname = (address, nickname) => {
  if (!isValidAddress(address)) {
    console.error('Invalid address:', address);
    return false;
  }
  
  const normalizedAddress = normalizeAddress(address);
  const addressBook = loadAddressBook();
  
  if (nickname && nickname.trim()) {
    addressBook[normalizedAddress] = nickname.trim();
  } else {
    delete addressBook[normalizedAddress];
  }
  
  saveAddressBook(addressBook);
  return true;
};

/**
 * Removes a nickname for an address
 * @param {string} address - Blockchain address
 * @returns {boolean} - True if successful
 */
const removeNickname = (address) => {
  if (!isValidAddress(address)) {
    console.error('Invalid address:', address);
    return false;
  }
  
  const normalizedAddress = normalizeAddress(address);
  const addressBook = loadAddressBook();
  
  delete addressBook[normalizedAddress];
  saveAddressBook(addressBook);
  return true;
};

/**
 * Gets all address-nickname pairs
 * @returns {Array} - Array of {address, nickname} objects
 */
const getAllEntries = () => {
  const addressBook = loadAddressBook();
  return Object.entries(addressBook).map(([address, nickname]) => ({
    address,
    nickname
  }));
};

/**
 * Clears all nicknames
 */
const clearAll = () => {
  saveAddressBook({});
};

/**
 * Exports the address book as JSON string
 * @returns {string} - JSON string of address book
 */
const exportAddressBook = () => {
  const addressBook = loadAddressBook();
  return JSON.stringify({
    version: '1.0',
    exportDate: new Date().toISOString(),
    addresses: addressBook
  }, null, 2);
};

/**
 * Imports address book from JSON string
 * @param {string} jsonData - JSON string to import
 * @param {boolean} merge - If true, merge with existing entries, if false replace all
 * @returns {Object} - Result object {success: boolean, message: string, imported: number}
 */
const importAddressBook = (jsonData, merge = true) => {
  try {
    const data = JSON.parse(jsonData);
    
    if (!data.addresses || typeof data.addresses !== 'object') {
      return { success: false, message: 'Invalid file format - missing addresses object', imported: 0 };
    }
    
    let currentAddressBook = merge ? loadAddressBook() : {};
    let imported = 0;
    
    // Validate and import addresses
    Object.entries(data.addresses).forEach(([address, nickname]) => {
      if (isValidAddress(address) && nickname && typeof nickname === 'string') {
        const normalizedAddress = normalizeAddress(address);
        currentAddressBook[normalizedAddress] = nickname.trim();
        imported++;
      }
    });
    
    saveAddressBook(currentAddressBook);
    
    const action = merge ? 'merged' : 'imported';
    return { 
      success: true, 
      message: `Successfully ${action} ${imported} address${imported !== 1 ? 'es' : ''}`, 
      imported 
    };
    
  } catch (error) {
    return { success: false, message: `Import failed: ${error.message}`, imported: 0 };
  }
};

export default {
  getNicknameOrAddress,
  getDisplayText,
  setNickname,
  removeNickname,
  getAllEntries,
  clearAll,
  exportAddressBook,
  importAddressBook,
  isValidAddress,
  normalizeAddress
};